import { Edit, TaskAlt, WarningAmber, ZoomIn, ZoomOut } from '@mui/icons-material';
import { Box, Checkbox, IconButton, Tooltip } from '@mui/material';
import { differenceInHours, format } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { Order, Shift } from '../../apiTypes';
import DataTable, { Column } from '../../components/DataTable';
import { DateRangeButtons } from '../../components/DateRangeButtons';
import Image from '../../components/Image';
import PrimaryButton from '../../components/PrimaryButton';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import { useCollection, useCollectionOnce } from '../../utils/api';
import currency from '../../utils/currency';
import { getMonthTimeRange, getWeekTimeRange } from '../../utils/dates';
import useMedia from '../../utils/useMedia';
import EditShift from '../Journal/EditShift';
import PaySalaryModal from './PaySalaryModal';

export type SalaryItem = DbDocument<Shift> & {
  totalOrdersSum: number;
  totalForSales: number;
  totalRate: number;
  total: number;
  hours: number;
  hasNotClosedOrders?: boolean;
};

type Row = SalaryItem;

const Salary = () => {
  const { isMobile } = useMedia();
  const { lang } = useLocale();
  const { workersMap, worker } = useCafe();
  const { cafeSlug } = useAdmin();
  const [scale, setScale] = useState<'week' | 'month'>('week');
  const [timeRange, setTimeRange] = useState<[number, number]>(getWeekTimeRange());
  const [selectedShifts, setSelectedShifts] = useState<string[]>([]);
  const [shift, setShift] = useState<DbDocument<Shift> | null>(null);
  const [isPayModalOpen, setIsPayModalOpen] = useState(false);

  const { data: allShifts } = useCollection<Shift>(`cafes/${cafeSlug}/shifts`, {
    orderBy: ['start', 'asc'],
    where: ['start', '>=', timeRange[0] || 0],
    where2: ['start', '<=', timeRange[1] || 0],
  });

  const { data: orders } = useCollectionOnce<Order>(`cafes/${cafeSlug}/orders`, {
    where: ['created', '>=', timeRange[0] || 0],
    where2: ['created', '<=', timeRange[1] || 0],
  });

  const shifts = useMemo(
    () =>
      worker?.credentials.view_other_employees_salary
        ? allShifts
        : (allShifts || []).filter((s) => s.workerId === worker?.id),
    [allShifts, worker?.credentials.view_other_employees_salary, worker?.id]
  );

  const rows = useMemo(() => {
    const rows: Row[] = (shifts || [])?.map((shift) => {
      const ownOrders = orders?.filter(
        (o) => o.workerId === shift.workerId && o.created > shift.start && o.created < shift.end
      );
      const hasNotClosedOrders = ownOrders?.some((o) => !o.paid) || false;
      const totalOrdersSum = ownOrders?.reduce((acc, o) => acc + (o.paid?.total || 0), 0) || 0;
      const totalForSales = shift.percentageOfSales ? (totalOrdersSum * shift.percentageOfSales) / 100 : 0;
      const hours = differenceInHours(shift.end, shift.start);
      const totalRate = hours * shift.ratePerHour;
      return {
        ...shift,
        hours,
        totalOrdersSum,
        totalForSales,
        totalRate,
        total: totalForSales + totalRate + shift.bonus,
        hasNotClosedOrders,
      };
    });

    return rows;
  }, [orders, shifts]);

  const selectedRows = useMemo(
    () => (selectedShifts.length > 0 ? rows.filter((r) => selectedShifts.includes(r.id)) : []),
    [rows, selectedShifts]
  );

  const canPay = useMemo(
    () =>
      worker?.credentials.perform_salary_payments &&
      selectedRows.length > 0 &&
      selectedRows.every((r) => !r.hasNotClosedOrders),
    [selectedRows, worker?.credentials.perform_salary_payments]
  );

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        name: 'checkbox',
        title: '',
        align: 'center',
        render: (r: Row) => {
          if (r.paid) {
            return <TaskAlt color="success" />;
          }
          if (r.hasNotClosedOrders) {
            return (
              <Tooltip title={t.unclosed_orders[lang]}>
                <WarningAmber color="warning" />
              </Tooltip>
            );
          }
          return (
            <Checkbox
              checked={selectedShifts.includes(r.id)}
              onChange={(e) => {
                if (e.target.checked) {
                  setSelectedShifts((prev) => [...prev, r.id]);
                } else {
                  setSelectedShifts((prev) => prev.filter((id) => id !== r.id));
                }
              }}
            />
          );
        },
        sx: { width: 40, pr: 0 },
      },
      {
        name: 'date',
        title: t.date[lang],
        align: 'left',
        render: (r: Row) => format(r.start, 'dd.MM'),
      },
      {
        name: 'imageURL',
        title: '',
        render: (r: Row) => <Image src={workersMap[r.workerId]?.photoUrl} rounded />,
        sx: { width: 40, pr: 0 },
      },
      {
        name: 'workerName',
        title: t.worker[lang],
        align: 'left',
        render: (r: Row) => workersMap[r.workerId]?.name,
        filterable: true,
      },
      {
        name: 'hours',
        title: t.hours[lang],
        render: (r: Row) => differenceInHours(r.end, r.start),
      },
      {
        name: 'rate_per_hour',
        title: t.rate_per_hour[lang],
        render: (r: Row) => (r.ratePerHour ? currency(r.ratePerHour) : ''),
      },
      {
        name: 'rate',
        title: t.rate[lang],
        render: (r: Row) => (r.totalRate ? currency(r.totalRate) : ''),
      },
      {
        name: 'percent',
        title: t.percentage_of_sales[lang],
        render: (r: Row) => (r.percentageOfSales ? `${r.percentageOfSales}%` : ''),
      },
      {
        name: 'commission_for_own_sales',
        title: t.commission_for_own_sales[lang],
        render: (r: Row) => (r.totalForSales ? currency(r.totalForSales) : ''),
      },
      {
        name: 'bonus',
        title: t.award_fine[lang],
        render: (r: Row) => (r.bonus ? currency(r.bonus) : ''),
      },
      {
        name: 'total',
        title: t.total[lang],
        render: (r: Row) => currency(r.total),
      },
      {
        name: 'paid',
        title: t.paid[lang],
        render: (r: Row) => (r.paid ? format(r.paid, 'dd.MM') : t.unpaid[lang]),
        filterable: true,
      },
      {
        name: 'edit',
        title: '',
        render: (r: Row) => (
          <IconButton
            onClick={() => setShift(r)}
            disabled={!worker?.credentials.journal || !worker?.credentials.edit_work_shifts_of_other_employees}
          >
            <Edit />
          </IconButton>
        ),
        filterable: true,
      },
    ],
    [
      lang,
      selectedShifts,
      worker?.credentials.edit_work_shifts_of_other_employees,
      worker?.credentials.journal,
      workersMap,
    ]
  );

  useEffect(() => {
    setTimeRange(scale === 'week' ? getWeekTimeRange() : getMonthTimeRange());
  }, [scale]);

  return (
    <Box sx={{ maxWidth: 1200, m: '0 auto' }}>
      <Box display="flex" justifyContent="space-between" flexDirection={isMobile ? 'column' : 'row'} mb={3}>
        <DateRangeButtons
          range={timeRange}
          setRange={(range) => {
            setSelectedShifts([]);
            setTimeRange(range);
          }}
          scale={scale}
        />
        <div>
          <IconButton onClick={() => setScale('month')} disabled={scale === 'month'}>
            <ZoomOut />
          </IconButton>
          <IconButton sx={{ mr: 2 }} onClick={() => setScale('week')} disabled={scale === 'week'}>
            <ZoomIn />
          </IconButton>
        </div>
      </Box>

      <DataTable
        columns={columns}
        rows={rows}
        dataTestIdPrefix="shift"
        actionButton={
          shifts &&
          shifts?.length > 0 && (
            <PrimaryButton variant="contained" disabled={!canPay} onClick={() => setIsPayModalOpen(true)}>
              {t.conduct_payment[lang]}
            </PrimaryButton>
          )
        }
      />

      {shift && <EditShift open shift={shift} onClose={() => setShift(null)} />}
      {isPayModalOpen && (
        <PaySalaryModal
          items={selectedRows}
          onClose={() => {
            setSelectedShifts([]);
            setIsPayModalOpen(false);
          }}
        />
      )}
    </Box>
  );
};

export default Salary;
